import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpDown, FileSpreadsheet } from "lucide-react";
import { api, errMsg, inr, fmtDate, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill } from "@/components/common";
import { TxnDrawer } from "@/components/TxnDrawer";
import { ClassifyForm } from "@/components/ClassifyForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const F = (v, set, items, ph, testid) => (
  <Select value={v || "all"} onValueChange={(x) => set(x === "all" ? "" : x)}>
    <SelectTrigger className="h-9 w-[150px]" data-testid={testid}><SelectValue placeholder={ph} /></SelectTrigger>
    <SelectContent><SelectItem value="all">{ph}: all</SelectItem>{items.map(([a, b]) => <SelectItem key={a} value={a}>{b}</SelectItem>)}</SelectContent>
  </Select>
);

function InlineCell({ t, field, list, onSaved, width = "w-32" }) {
  const [v, setV] = useState(t[field] || "");
  const locked = ["exported", "imported"].includes(t.export_status);
  useEffect(() => { setV(t[field] || ""); }, [t, field]);
  const save = async () => {
    if ((t[field] || "") === v) return;
    try { await api.patch(`/transactions/${t.id}`, { [field]: v }); onSaved(); } catch (e) { toast.error(await errMsg(e)); setV(t[field] || ""); }
  };
  return (
    <input value={v} disabled={locked} list={list} onClick={(e) => e.stopPropagation()} onChange={(e) => setV(e.target.value)} onBlur={save}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(t[field] || ""); e.currentTarget.blur(); } }}
      className={`${width} rounded border border-transparent bg-transparent px-1.5 py-1 text-xs transition-colors hover:border-border focus:border-primary focus:bg-background focus:outline-none disabled:opacity-60`}
      data-testid={`cell-${field}-${t.id}`} />
  );
}

export default function Transactions() {
  const { accounts, ledgers, meta } = useApp();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: "", status: sp.get("status") || "", dup_status: sp.get("dup_status") || "", direction: "", account_id: "", date_from: "", date_to: "", amount: "" });
  const [sort, setSort] = useState(["txn_date", -1]);
  const [data, setData] = useState({ total: 0, items: [] });
  const [limit, setLimit] = useState(200);
  const [sel, setSel] = useState(new Set());
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(sp.get("open"));
  const [bulkOpen, setBulkOpen] = useState(false);
  const search = useRef();
  const load = useCallback(() => api.get("/transactions", { params: { ...f, sort: sort[0], order: sort[1], limit } }).then((r) => setData(r.data)), [f, sort, limit]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const h = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || open || bulkOpen) return;
      if (e.key === "/") { e.preventDefault(); search.current?.focus(); }
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, data.items.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      if (e.key === "Enter" && data.items[active]) setOpen(data.items[active].id);
      if (e.key === " " && data.items[active]) { e.preventDefault(); toggle(data.items[active].id); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  useEffect(() => { document.querySelector(`[data-row-index="${active}"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const bulk = async (action) => {
    try {
      const r = await api.post("/transactions/bulk", { ids: [...sel], action });
      toast[r.data.errors.length ? "warning" : "success"](`${r.data.done} updated${r.data.errors.length ? ` · ${r.data.errors.length} skipped: ${r.data.errors[0].error}` : ""}`);
      setSel(new Set());
      load();
    } catch (e) { toast.error(await errMsg(e)); }
  };
  const th = (label, key, cls = "") => (
    <th className={`px-2 py-2.5 font-medium ${cls}`}>
      {key ? <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setSort(([k, o]) => [key, k === key ? -o : -1])} data-testid={`sort-${key}`}>{label}<ArrowUpDown className="h-3 w-3" /></button> : label}
    </th>
  );
  const selected = data.items.filter((t) => sel.has(t.id));
  return (
    <div>
      <PageHeader title="All Transactions" sub={`${data.total} rows · narrations shown exactly as printed · click a cell to edit, ↑↓ to move, Enter to open, Space to select.`}>
        <Button variant="outline" onClick={() => download("/transactions-export.xlsx", { params: f })} data-testid="export-excel-button"><FileSpreadsheet className="mr-2 h-4 w-4" />Excel</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-2">
        <Input ref={search} className="h-9 w-64" placeholder="Search narration, UTR, party, bill… (/)" value={f.q} onChange={set("q")} data-testid="transaction-table-search-input" />
        <Input className="num h-9 w-32" placeholder="Exact ₹" value={f.amount} onChange={set("amount")} data-testid="amount-filter-input" />
        {F(f.status, set("status"), [["needs_review", "Needs review"], ["classified", "Awaiting approval"], ["approved", "Approved"], ["suspense", "Suspense"], ["duplicate", "Duplicate"]], "Status", "status-filter")}
        {F(f.dup_status, set("dup_status"), [["new", "New"], ["possible_duplicate", "Possible duplicate"], ["confirmed_duplicate", "Confirmed duplicate"], ["already_imported", "Already imported"]], "Duplicates", "dup-filter")}
        {F(f.direction, set("direction"), [["debit", "Debit"], ["credit", "Credit"]], "Dr/Cr", "direction-filter")}
        {F(f.account_id, set("account_id"), accounts.map((a) => [a.id, `${a.bank_name} ${a.masked}`]), "Bank", "account-filter")}
        <Input type="date" className="h-9 w-40" value={f.date_from} onChange={set("date_from")} data-testid="date-from-filter" />
        <Input type="date" className="h-9 w-40" value={f.date_to} onChange={set("date_to")} data-testid="date-to-filter" />
      </div>
      {sel.size > 0 && (
        <div className="glass sticky top-16 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-xl border p-3 fade-up" data-testid="bulk-action-bar">
          <span className="num text-sm">{sel.size} selected</span>
          <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-classify-button">Classify</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("approve")} data-testid="bulk-approve-button">Approve</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("suspense")} data-testid="bulk-suspense-button">Send to suspense</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("confirm_duplicate")} data-testid="bulk-confirm-dup-button">Confirm duplicate</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("not_duplicate")} data-testid="bulk-not-dup-button">Not duplicate</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("verify_extraction")} data-testid="bulk-verify-button">Verified vs PDF</Button>
          <Button size="sm" variant="ghost" onClick={() => setSel(new Set())}>Clear</Button>
        </div>
      )}
      <datalist id="grid-ledgers">{ledgers.map((l) => <option key={l.id} value={l.name} />)}</datalist>
      <datalist id="grid-vouchers">{meta.voucher_types.map((v) => <option key={v} value={v} />)}</datalist>
      <div className="panel max-h-[70vh] overflow-auto" data-testid="transactions-grid">
        <table className="w-full min-w-[1400px] text-xs">
          <thead className="glass sticky top-0 z-10 text-left text-muted-foreground">
            <tr>
              <th className="px-2"><Checkbox checked={data.items.length > 0 && sel.size === data.items.length} onCheckedChange={(v) => setSel(v ? new Set(data.items.map((t) => t.id)) : new Set())} data-testid="select-all-checkbox" /></th>
              {th("Date", "txn_date")}{th("Narration (original)")}{th("Ref / UTR")}{th("Debit", "amount_paise", "text-right")}{th("Credit", null, "text-right")}{th("Balance", null, "text-right")}
              {th("Party", "party_name")}{th("Ledger", "ledger")}{th("Voucher")}{th("Bill no")}{th("Remarks")}{th("Status", "status")}{th("Duplicate")}{th("Export")}
            </tr>
          </thead>
          <tbody>
            {data.items.map((t, i) => {
              const a = accounts.find((x) => x.id === t.account_id);
              return (
                <tr key={t.id} data-row-index={i} onClick={() => { setActive(i); setOpen(t.id); }} data-testid="transaction-row"
                  className={`cursor-pointer border-t transition-colors hover:bg-accent/60 ${i === active ? "row-active" : ""}`}>
                  <td className="px-2" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.has(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`row-checkbox-${t.id}`} /></td>
                  <td className="num whitespace-nowrap px-2 py-1.5">{fmtDate(t.txn_date)}<div className="text-[10px] text-muted-foreground">{a?.bank_name?.split(" ")[0]} {a?.masked}</div></td>
                  <td className="max-w-[300px] px-2 font-mono" title={t.narration}><div className="truncate">{t.narration}</div>
                    {(t.tags || []).length > 0 && <div className="mt-0.5 flex gap-1">{t.tags.map((g) => <span key={g} className="rounded bg-accent px-1 text-[9px] uppercase">{g.replace("_", " ")}</span>)}</div>}</td>
                  <td className="num max-w-[120px] truncate px-2 text-muted-foreground">{t.ref}</td>
                  <td className="num px-2 text-right text-rose-600 dark:text-rose-400">{Number(t.debit) ? inr(t.debit, false) : ""}</td>
                  <td className="num px-2 text-right text-emerald-600 dark:text-emerald-400">{Number(t.credit) ? inr(t.credit, false) : ""}</td>
                  <td className="num px-2 text-right">{inr(t.balance, false)}</td>
                  <td className="px-2">{t.party_name || <span className="italic text-muted-foreground">{t.party_extracted || t.ai_suggestion?.party || "?"}</span>}
                    {!t.party_id && t.party_confidence > 0 && <span className="ml-1 text-[10px] text-amber-500">{Math.round(t.party_confidence * 100)}%</span>}</td>
                  <td className="px-1"><InlineCell t={t} field="ledger" list="grid-ledgers" onSaved={load} width="w-36" /></td>
                  <td className="px-1"><InlineCell t={t} field="voucher_type" list="grid-vouchers" onSaved={load} width="w-20" /></td>
                  <td className="px-1"><InlineCell t={t} field="bill_no" onSaved={load} width="w-24" /></td>
                  <td className="px-1"><InlineCell t={t} field="remarks" onSaved={load} width="w-28" /></td>
                  <td className="px-2"><Pill s={t.status} /></td>
                  <td className="px-2"><Pill s={t.dup_status} /></td>
                  <td className="px-2"><Pill s={t.export_status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.items.length === 0 && <div className="p-8 text-sm text-muted-foreground">No transactions match these filters.</div>}
      </div>
      {data.total > data.items.length && <Button variant="outline" className="mt-3" onClick={() => setLimit((l) => l + 200)} data-testid="load-more-button">Load more ({data.total - data.items.length} remaining)</Button>}
      <TxnDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Classify {selected.length} entries</DialogTitle></DialogHeader>
          {selected.length > 0 && <ClassifyForm txns={selected} onDone={() => { setBulkOpen(false); setSel(new Set()); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
